import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

export type ButtonVariant = 'primary' | 'ghost' | 'soft' | 'danger' | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg';

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  block?: boolean;
  loading?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
  children?: ReactNode;
};

const sizeClass: Record<ButtonSize, string> = {
  sm: 'btn-sm',
  md: '',
  lg: 'btn-lg',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    block,
    loading,
    leadingIcon,
    trailingIcon,
    type = 'button',
    className = '',
    disabled,
    children,
    ...rest
  },
  ref,
) {
  const cls = ['btn', `btn-${variant}`, sizeClass[size], block ? 'btn-block' : '', className]
    .filter(Boolean)
    .join(' ');

  return (
    <button ref={ref} type={type} className={cls} disabled={disabled || loading} aria-busy={loading || undefined} {...rest}>
      {loading ? <Spinner /> : leadingIcon}
      {children ? <span className="btn-label">{children}</span> : null}
      {!loading ? trailingIcon : null}
    </button>
  );
});

function Spinner() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true" className="btn-spinner" style={{ animation: 'btn-spin 0.9s linear infinite' }}>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2.5" fill="none" strokeOpacity="0.25" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" fill="none" />
    </svg>
  );
}
